import axios from 'axios';
import emailjs from '@emailjs/browser';
import config from '../config';

const AUTH_TOKEN_KEY = 'promptforge_token';
const AUTH_USER_KEY = 'promptforge_user';

const authClient = axios.create({
  baseURL: config.api.baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const getToken = () => {
  return localStorage.getItem(AUTH_TOKEN_KEY);
};

export const getUser = () => {
  const user = localStorage.getItem(AUTH_USER_KEY);
  return user ? JSON.parse(user) : null;
};

export const isAuthenticated = () => {
  return !!getToken();
};

const storeAuthData = (token, user) => {
  localStorage.setItem(AUTH_TOKEN_KEY, token);
  localStorage.setItem(AUTH_USER_KEY, JSON.stringify(user));
};

export const clearAuthData = () => {
  localStorage.removeItem(AUTH_TOKEN_KEY);
  localStorage.removeItem(AUTH_USER_KEY);
};

export const checkAvailability = async (username, email) => {
  try {
    const response = await authClient.post('/api/v1/auth/check-availability', { username, email });
    return {
      success: true,
      available: response.data.available,
      username_available: response.data.username_available,
      email_available: response.data.email_available,
      message: response.data.message,
    };
  } catch (error) {
    const message = error.response?.data?.detail || 'Failed to check availability';
    return { success: false, error: message };
  }
};

export const sendOTP = async (email) => {
  try {
    // Get OTP from backend
    const response = await authClient.post('/api/v1/auth/send-otp', { email });
    const otpCode = response.data.otp_code;
    
    if (!otpCode) {
      return { success: false, error: 'Failed to generate verification code' };
    }
    
    // Check if EmailJS is configured
    if (!config.emailjs.serviceId || !config.emailjs.templateId || !config.emailjs.publicKey) {
      return { success: false, error: 'Email service not configured. Please add EmailJS environment variables.' };
    }
    
    // Send email via EmailJS
    await emailjs.send(
      config.emailjs.serviceId,
      config.emailjs.templateId,
      {
        to_email: email,
        otp_code: otpCode,
      },
      config.emailjs.publicKey
    );
    
    return { success: true, message: 'Verification code sent to your email' };
  } catch (error) {
    const message = error.response?.data?.detail || error.text || 'Failed to send verification code';
    return { success: false, error: message };
  }
};

export const verifyOTP = async (email, otp) => {
  try {
    const response = await authClient.post('/api/v1/auth/verify-otp', { email, otp });
    return { success: true, message: response.data.message };
  } catch (error) {
    const message = error.response?.data?.detail || 'Invalid verification code';
    return { success: false, error: message };
  }
};

export const register = async (userData) => {
  try {
    const response = await authClient.post('/api/v1/auth/register', userData);
    const { access_token, user } = response.data;
    storeAuthData(access_token, user);
    return { success: true, user };
  } catch (error) {
    const message = error.response?.data?.detail || 'Registration failed';
    return { success: false, error: message };
  }
};

export const login = async (credentials) => {
  try {
    const response = await authClient.post('/api/v1/auth/login', credentials);
    const { access_token, user } = response.data;
    storeAuthData(access_token, user);
    return { success: true, user };
  } catch (error) {
    const message = error.response?.data?.detail || 'Login failed';
    return { success: false, error: message };
  }
};

export const googleLogin = async (credential) => {
  try {
    const response = await authClient.post('/api/v1/auth/google', { credential });
    const { access_token, user } = response.data;
    storeAuthData(access_token, user);
    return { success: true, user };
  } catch (error) {
    const message = error.response?.data?.detail || 'Google sign-in failed';
    return { success: false, error: message };
  }
};

export const logout = () => {
  clearAuthData();
};

export const getCurrentUser = async () => {
  const token = getToken();
  if (!token) return null;

  try {
    const response = await authClient.get('/api/v1/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (error) {
    clearAuthData();
    return null;
  }
};

const authService = {
  getToken,
  getUser,
  isAuthenticated,
  sendOTP,
  verifyOTP,
  register,
  login,
  googleLogin,
  logout,
  getCurrentUser,
  clearAuthData,
};

export default authService;

